/**
 * Yardage calculation utilities for punch needle projects
 */

const YARDS_PER_SKEIN = 8.7 // DMC 6-strand floss
const WASTE_BUFFER = 1.1 // 10% buffer for waste

/**
 * Calculate estimated yardage for each color in the project
 * @param {object} project - Project object with grid, palette, and settings
 * @param {object} options - Thread settings
 * @param {string} options.threadType - 'stranded' or 'perle'
 * @param {number} options.strandsUsed - Number of strands used for stranded floss
 * @returns {array} Array of yardage data for each color
 */
export const calculateYardage = (
  project,
  { threadType = 'stranded', strandsUsed = 6 } = {}
) => {
  if (!project.grid || project.grid.length === 0) {
    return []
  }

  let effectiveYardsPerSkein = YARDS_PER_SKEIN

  if (threadType === 'stranded') {
    effectiveYardsPerSkein = YARDS_PER_SKEIN * (6 / strandsUsed)
  }

  const colorCounts = {}

  // Count cells for each color
  project.grid.forEach((row) => {
    row.forEach((cell) => {
      if (cell.colorId) {
        if (!colorCounts[cell.colorId]) {
          colorCounts[cell.colorId] = {
            count: 0,
            heightFactors: [],
          }
        }
        colorCounts[cell.colorId].count++
        colorCounts[cell.colorId].heightFactors.push(
          getHeightMultiplier(cell.loopHeight)
        )
      }
    })
  })

  // Calculate yardage for each color
  return project.palette
    .map((color) => {
      const colorData = colorCounts[color.code]
      if (!colorData) {
        return {
          color,
          cellCount: 0,
          yards: 0,
          skeins: 0,
          yardsPerUnit: effectiveYardsPerSkein,
        }
      }

      const avgHeightMultiplier =
        colorData.heightFactors.reduce((sum, val) => sum + val, 0) /
        colorData.heightFactors.length

      const inchesPerCell = 1 / project.stitchesPerInch
      const totalInches =
        colorData.count * inchesPerCell * avgHeightMultiplier * WASTE_BUFFER
      const yards = totalInches / 36

      const skeins = Math.ceil(yards / effectiveYardsPerSkein)

      return {
        color,
        cellCount: colorData.count,
        yards,
        skeins,
        yardsPerUnit: effectiveYardsPerSkein,
      }
    })
    .filter((item) => item.cellCount > 0)
}

/**
 * Get height multiplier for loop height
 * @param {string} loopHeight - Loop height value
 * @returns {number} Multiplier for yarn usage
 */
const getHeightMultiplier = (loopHeight) => {
  const multipliers = {
    short: 0.75,
    medium: 1.0,
    tall: 1.5,
  }
  return multipliers[loopHeight] || 1.0
}

/**
 * Calculate total project yardage
 * @param {object} project - Project object
 * @param {object} options - Thread settings
 * @returns {number} Total yards needed
 */
export const calculateTotalYardage = (project, options = {}) => {
  const yardageData = calculateYardage(project, options)
  return yardageData.reduce((sum, item) => sum + item.yards, 0)
}